const API_BASE = '/api/v1/settings';

export interface Settings {
  llm_provider: string;
  llm_model: string;
  ollama_base_url: string;
  anthropic_api_key?: string;
  openai_api_key?: string;
  auto_route_images?: string;
  auto_route_documents?: string;
  strava_client_id?: string;
  strava_client_secret?: string;
}

export interface OllamaModel {
  name: string;
  size: string;
}

export interface LLMStatus {
  status: 'connected' | 'disconnected' | 'configured' | 'not_configured' | 'unknown';
  provider: string;
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Errore sconosciuto');
  }
  return data;
}

export async function getSettings(): Promise<{ success: boolean; settings: Settings }> {
  const response = await fetch(API_BASE, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function updateSettings(
  settings: Partial<Settings>
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(API_BASE, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(settings),
  });
  return handleResponse(response);
}

export async function getOllamaModels(): Promise<{
  success: boolean;
  models?: OllamaModel[];
  error?: string;
}> {
  const response = await fetch(`${API_BASE}/ollama/models`, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function getLLMStatus(): Promise<{
  success: boolean;
  status: LLMStatus['status'];
  provider: string;
}> {
  const response = await fetch(`${API_BASE}/llm/status`, {
    credentials: 'include',
  });
  return handleResponse(response);
}

// Database types
export interface DatabaseStats {
  size: string;
  sizeBytes: number;
  tables: number;
  name: string;
  backupPath: string;
}

export interface BackupFile {
  name: string;
  size: string;
  sizeBytes: number;
  createdAt: string;
  path: string;
}

// Database API functions
export async function getDatabaseStats(): Promise<{
  success: boolean;
  stats: DatabaseStats;
}> {
  const response = await fetch(`${API_BASE}/database/stats`, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function getBackups(): Promise<{
  success: boolean;
  backups: BackupFile[];
  backupPath: string;
}> {
  const response = await fetch(`${API_BASE}/database/backups`, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function createBackup(path?: string): Promise<{
  success: boolean;
  backup?: BackupFile;
  message?: string;
  error?: string;
}> {
  const response = await fetch(`${API_BASE}/database/backup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ path }),
  });
  return handleResponse(response);
}

export async function restoreBackup(backupPath: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const response = await fetch(`${API_BASE}/database/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ backupPath }),
  });
  return handleResponse(response);
}

export async function deleteBackup(filename: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const response = await fetch(`${API_BASE}/database/backups/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  return handleResponse(response);
}

// Filesystem types and functions
export interface DirectoryEntry {
  name: string;
  path: string;
}

export async function browseFilesystem(path?: string): Promise<{
  success: boolean;
  currentPath: string;
  parentPath: string;
  directories: DirectoryEntry[];
}> {
  const url = path
    ? `${API_BASE}/filesystem/browse?path=${encodeURIComponent(path)}`
    : `${API_BASE}/filesystem/browse`;
  const response = await fetch(url, {
    credentials: 'include',
  });
  return handleResponse(response);
}

// Admin types
export interface UserStats {
  documentsCount: number;
  conversationsCount: number;
  labParametersCount: number;
  diskSpaceBytes: number;
  diskSpaceFormatted: string;
}

export interface UserLLMConfig {
  provider: string;
  model: string;
  hasCustomConfig: boolean;
}

export type UserStatus = 'PENDING' | 'ACTIVE' | 'REJECTED';

export interface UserWithStats {
  id: string;
  email: string;
  name: string | null;
  role: 'ADMIN' | 'MEMBER';
  status: UserStatus;
  forcePasswordChange: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  stats: UserStats;
  llmConfig: UserLLMConfig;
}

// Admin API functions
export async function getAdminUsersStats(): Promise<{
  success: boolean;
  users: UserWithStats[];
}> {
  const response = await fetch('/api/v1/admin/users/stats', {
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function forcePasswordChange(userId: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const response = await fetch(`/api/v1/admin/users/${userId}/force-password-change`, {
    method: 'POST',
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function deleteUser(userId: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const response = await fetch(`/api/v1/admin/users/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function approveUser(userId: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const response = await fetch(`/api/v1/admin/users/${userId}/approve`, {
    method: 'POST',
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function rejectUser(userId: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const response = await fetch(`/api/v1/admin/users/${userId}/reject`, {
    method: 'POST',
    credentials: 'include',
  });
  return handleResponse(response);
}
